// Native-auth infrastructure for the Apple + Google + verified-phone posture.
//
// The security-critical plumbing the native sign-in card in login.jsx consumes: OAuth
// state/CSRF, native Google sign-in through ASWebAuthenticationSession (QlearnAuthSession) with
// callback validation, the JWT-shape guard, the provider boundary, and the native Apple authorize
// seam. Only the native card (isNativeCapacitorRuntime() plus REACT_APP_QLEARN_IOS_SIGNIN_TRIO)
// calls it, so the web Google/Phone sign-in is unchanged.
import { api } from './apiService';
import { isTokenPersistable } from '../utils/session';

// Custom URL scheme the native app registers (iOS Info.plist CFBundleURLTypes);
// the backend redirects the OAuth callback here so iOS routes it back in-app.
export const NATIVE_OAUTH_REDIRECT_URI = 'com.medlearnity.qlearn://oauth2redirect';

export const OAUTH_PROVIDERS = Object.freeze({ GOOGLE: 'google', APPLE: 'apple' });

// C3: a usable FRESH provider token — three STRICT base64url segments (fresh
// Google/Apple tokens are never legacy-base64; stored-token tolerance lives in
// utils/session, not here), with the claims contract DELEGATED to the one
// shared predicate: utils/session.isTokenPersistable (typed NumericDate `exp`
// in the future). Delegation-by-construction closes the round-3 class — a
// parallel local claims check here is exactly how coercive/expired exp shapes
// kept slipping one guard while the other was cured.
export const isLikelyJwt = (token) => {
  if (typeof token !== 'string' ||
      !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token)) {
    return false;
  }
  return isTokenPersistable(token);
};

// C1: unguessable opaque state correlating an OAuth request with its callback.
// CSPRNG only — a predictable state defeats the OAuth CSRF guard, so we never
// derive it from a non-cryptographic PRNG (Math.random). Browsers and the native
// WKWebView runtime always have Web Crypto; without it we throw (fail closed)
// rather than fall back. There is deliberately no Node `crypto` import: webpack 4
// would bundle crypto-browserify (~0.9 MB) into every page for a branch no
// browser takes. Yields 16 opaque, per-request random bytes as 32 hex chars.
export const generateOAuthState = () => {
  const toHex = (bytes) =>
    Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  const webCrypto =
    (typeof window !== 'undefined' && (window.crypto || window.msCrypto)) ||
    (typeof globalThis !== 'undefined' && globalThis.crypto) ||
    null;
  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    webCrypto.getRandomValues(bytes);
    return toHex(bytes);
  }
  throw new Error('OAuth state needs a Web Crypto CSPRNG (crypto.getRandomValues)');
};

// Strictly validates the native OAuth callback URL (the one QlearnAuthSession's start() resolves)
// before trusting its token:
// exact scheme+host (never a prefix), state matches the request we sent, and the
// token is a well-formed JWT. Returns { ok, token } — ok:false rejects everything
// (forged host, login-CSRF/injected state, or non-JWT).
export const parseNativeOAuthCallback = (openedUrl, expectedState) => {
  try {
    const parsed = new URL(openedUrl);
    const expected = new URL(NATIVE_OAUTH_REDIRECT_URI);
    if (parsed.protocol !== expected.protocol || parsed.host !== expected.host) {
      return { ok: false };
    }
    if (!expectedState || parsed.searchParams.get('state') !== expectedState) {
      return { ok: false };
    }
    const token = parsed.searchParams.get('token');
    if (!isLikelyJwt(token)) {
      return { ok: false };
    }
    return { ok: true, token };
  } catch (err) {
    return { ok: false };
  }
};

// ASAuthorizationError.canceled is the EXACT code 1001. Classification is CODE-FIRST and
// PRESENCE-DEFINITIVE: if the error carries a `code` PROPERTY at all — even '' / null / 0 /
// any other falsey value — only String(code) === '1001' is a cancel and the message can
// NEVER override it (a non-1001 code is always a real error). Only when there is NO `code`
// property do we consult the message, and then solely an explicitly-typed STRING whose WHOLE
// value (after light normalisation) is one of a narrow allowlist of clean user-cancel
// phrases — never a substring, so negation, service/failure/upstream context, numeric/string
// lookalikes, and any suffix/prefix text all fail, and a non-string message (e.g. an object
// with a coercing toString) is refused outright. The pinned plugin rejects with only
// localizedDescription (no code), so an ambiguous failure surfaces honestly as an error
// rather than a silent cancel.
// The exact cancel code, in its two allowed PRIMITIVE representations. Comparison is strict
// (`===`) with no coercion, so object/array/boxed-Number/bigint/toString/valueOf lookalikes
// (e.g. { toString: () => '1001' }, [1001], new Number(1001), 1001n) never match.
const APPLE_CANCEL_CODE_STR = '1001';
const APPLE_CANCEL_CODE_NUM = 1001;

// Whole-string allowlist of clean user-cancel phrases. Applied to a normalised message
// (trimmed, lowercased, a single run of trailing '.'/'!' removed) and anchored ^…$, so ANY
// extra context — leading "no ", trailing " after a network error", ": E1001_SERVER",
// "(error 10010)", "; code 21001" — fails to match.
const CLEAN_USER_CANCEL = new RegExp(
  '^(?:'
  + 'the user cancel(?:l?ed)?(?: the (?:request|sign-in flow|sign-in|authorization attempt|authorization))?'
  + '|authorization cancel(?:l?ed)? by the user'
  + '|the request was cancel(?:l?ed)? by user'
  + '|cancel(?:l?ed)? by the user'
  + ')$',
);

const isCleanUserCancelMessage = (message) => {
  if (typeof message !== 'string') return false; // typed: only a real string may cancel
  const normalized = message.trim().toLowerCase().replace(/[.!]+$/, '').trim();
  return CLEAN_USER_CANCEL.test(normalized);
};

export const isAppleAuthCancel = (err) => {
  if (err === null || err === undefined) return false;
  if (typeof err === 'string') return isCleanUserCancelMessage(err);
  if (typeof err !== 'object') return false; // numbers/booleans/etc. are never a cancel
  if ('code' in err) {
    // Property presence is definitive — the code decides and the message is NOT consulted.
    // STRICT primitive equality only: never coerce with String(err.code), so a non-primitive
    // code whose toString/valueOf yields 1001 cannot silently cancel a real error.
    const code = err.code;
    return code === APPLE_CANCEL_CODE_STR || code === APPLE_CANCEL_CODE_NUM;
  }
  return isCleanUserCancelMessage(err.message); // no code property → typed whole-string only
};

// Builds the native Google OAuth start URL (ASWebAuthenticationSession → backend redirect to
// the custom scheme). A referral code is added when one is passed; on native the caller's
// getActiveReferralCode returns none (iOS ruling B5).
export const buildNativeGoogleAuthUrl = ({ domain, state, referralCode, referralCapturedAt }) => {
  const params = new URLSearchParams({ redirect_uri: NATIVE_OAUTH_REDIRECT_URI, state });
  if (referralCode) {
    params.set('ref', referralCode);
    if (referralCapturedAt) params.set('refAt', referralCapturedAt);
  }
  return `${domain}/qlearn/oauth2/authorization/google?${params.toString()}`;
};

// ---- Native Google OAuth (iOS ruling C2(b)): ASWebAuthenticationSession via QlearnAuthSession ----
//
// The iOS shell's QlearnAuthSession plugin runs the authorization URL in an
// ASWebAuthenticationSession whose callback scheme is ours, so THIS flow's callback reaches only
// this app, and start() resolves with the full callback URL. There is no appUrlOpen listener and
// no @capacitor/browser sheet: the token is read only from start()'s own result, and
// parseNativeOAuthCallback (exact scheme+host, state, JWT shape) stays the gate.
//
// Without the plugin the native Google flow FAILS CLOSED. Falling back to the system browser
// would reopen C2: any app can claim the custom scheme and receive the token.
//
// Plugin contract (aksg87/qlearn-unified #218): start({ url, callbackScheme }) resolves { url };
// it rejects with code CANCELLED (user closed the sheet, or cancel() was called), IN_PROGRESS,
// INVALID_URL, INVALID_SCHEME or FAILED. cancel() rejects a pending start() with CANCELLED at
// once, and a start() right after a cancel() is accepted (the plugin retries iOS's short refusal).
export const NATIVE_AUTH_SESSION_PLUGIN = 'QlearnAuthSession';
export const NATIVE_OAUTH_CALLBACK_SCHEME = 'com.medlearnity.qlearn';
const NATIVE_RETIRE_BOUND_MS = 3000;
const NATIVE_LOAD_BOUND_MS = 5000;

let authSessionPlugin = null;
// Resolves { plugin } with the registered plugin, or null when this build has none (or off the
// native runtime). The plugin is WRAPPED on purpose: a Capacitor plugin proxy answers every
// property as a native method, `then` included, so a promise resolved with the bare proxy calls
// plugin.then() on the bridge ("not implemented") and never settles. Never await, return from
// an async function, or Promise.resolve() the bare plugin.
const loadNativeAuthSession = async () => {
  const { Capacitor, registerPlugin } = await import('@capacitor/core');
  if (!Capacitor || typeof Capacitor.isPluginAvailable !== 'function'
      || !Capacitor.isPluginAvailable(NATIVE_AUTH_SESSION_PLUGIN)) {
    return null;
  }
  if (!authSessionPlugin) authSessionPlugin = registerPlugin(NATIVE_AUTH_SESSION_PLUGIN);
  return { plugin: authSessionPlugin };
};

// The user closed the sheet (or the attempt was cancelled): a silent outcome, like an Apple cancel.
export const isNativeAuthSessionCancel = (err) => Boolean(err) && typeof err === 'object' && err.code === 'CANCELLED';

// One live attempt at a time: admissions run one after another (a fast double tap cannot start
// two sessions), and a new attempt cancels the previous one and waits (bounded) for its start()
// to settle before opening a sheet. Each attempt calls cancel() at most once, so a late dispose of
// a superseded attempt can never cancel its successor's session.
let activeAttempt = null;
let admissionChain = Promise.resolve();
const withAdmission = (fn) => {
  const run = admissionChain.then(fn, fn);
  admissionChain = run.then(() => {}, () => {});
  return run;
};

// Test-only: forget the active attempt, the admission chain and the cached plugin between specs.
export const __resetNativeGoogleOAuthForTests = () => {
  activeAttempt = null;
  admissionChain = Promise.resolve();
  authSessionPlugin = null;
};

const failedController = (error) => ({
  result: Promise.resolve({ ok: false, error }),
  dispose: async () => {},
  state: null,
  authorizationUrl: null,
});

// Resolves with the promise's value, or with `fallback` after boundMs; never rejects.
const valueWithin = (promise, boundMs, fallback) => new Promise((resolve) => {
  const timer = setTimeout(() => resolve(fallback), boundMs);
  promise.then(
    (value) => { clearTimeout(timer); resolve(value); },
    () => { clearTimeout(timer); resolve(fallback); },
  );
});

// Resolves a controller { result, dispose, state, authorizationUrl }:
//   result: Promise<{ ok: true, token } | { ok: false, cancelled?: true, error }>, never rejects,
//     and always settles once dispose() returns;
//   dispose(): cancels the attempt if it is still open (at most one cancel()); idempotent.
const admitNativeGoogleOAuth = async ({
  domain,
  referralCode,
  referralCapturedAt,
  generateState = generateOAuthState,
  loadAuthSession = loadNativeAuthSession,
  retireBoundMs = NATIVE_RETIRE_BOUND_MS,
  loadBoundMs = NATIVE_LOAD_BOUND_MS,
} = {}) => {
  let loaded = null;
  try { loaded = await valueWithin(Promise.resolve().then(loadAuthSession), loadBoundMs, null); } catch (e) { loaded = null; }
  const session = loaded && typeof loaded === 'object' ? loaded.plugin : null;
  if (!session || typeof session.start !== 'function') {
    return failedController(new Error('QlearnAuthSession is unavailable: native Google sign-in fails closed'));
  }
  if (activeAttempt) await activeAttempt.retire();

  const state = generateState();
  const authorizationUrl = buildNativeGoogleAuthUrl({ domain, state, referralCode, referralCapturedAt });
  let disposed = false;
  let startSettled = false;
  let cancelCall = null;
  let resolveResult;
  const result = new Promise((resolve) => { resolveResult = resolve; });
  let resultSettled = false;
  const settleResult = (outcome) => { if (!resultSettled) { resultSettled = true; resolveResult(outcome); } };
  let startCall;
  try {
    startCall = session.start({ url: authorizationUrl, callbackScheme: NATIVE_OAUTH_CALLBACK_SCHEME });
  } catch (e) {
    startCall = Promise.reject(e);
  }
  // start() returns the bridge's own promise; only its { url } value is read, never the plugin.
  const startOutcome = Promise.resolve(startCall).then(
    (res) => {
      if (disposed) return { ok: false, error: new Error('sign-in attempt disposed') };
      const parsed = parseNativeOAuthCallback(res && res.url, state);
      return parsed.ok ? { ok: true, token: parsed.token } : { ok: false, error: new Error('native OAuth callback rejected') };
    },
    (error) => (isNativeAuthSessionCancel(error) ? { ok: false, cancelled: true, error } : { ok: false, error }),
  );
  const startSettledSignal = startOutcome.then((outcome) => { startSettled = true; settleResult(outcome); });

  const self = {
    retire: async () => {
      disposed = true;
      if (!startSettled && !cancelCall && typeof session.cancel === 'function') {
        try { cancelCall = Promise.resolve(session.cancel()).catch(() => {}); } catch (e) { cancelCall = Promise.resolve(); }
      }
      await valueWithin(startSettledSignal, retireBoundMs, undefined);
      settleResult({ ok: false, error: new Error('sign-in attempt disposed') });
    },
  };
  activeAttempt = self;
  startSettledSignal.then(() => { if (activeAttempt === self) activeAttempt = null; });

  return { result, dispose: () => self.retire(), state, authorizationUrl };
};

export const runNativeGoogleOAuth = (params) => withAdmission(() => admitNativeGoogleOAuth(params));

// Native Sign in with Apple seam, aligned to the EXACT pinned
// @capacitor-community/apple-sign-in@8.0.0 contract. Its options are
// { clientId, redirectURI, scopes?: string, state?, nonce? } and the iOS Swift reads
// `call.getString("scopes")` then `.contains("name")` / `.contains("email")` — an array
// (the old `requestedScopes: [0,1]`) is silently ignored, so full-name/email would never be
// requested. We therefore send the `scopes` STRING the plugin actually reads. On error the
// plugin rejects with only `error.localizedDescription` (no native code), so we let that
// rejection propagate UNCHANGED for the caller's isAppleAuthCancel to classify honestly.
export const APPLE_SCOPES = 'name email';

export const authorizeApple = async ({
  importer = () => import('@capacitor-community/apple-sign-in'),
  clientId,
  redirectURI,
  state,
  nonce,
} = {}) => {
  const { SignInWithApple } = await importer();
  const { response } = await SignInWithApple.authorize({
    scopes: APPLE_SCOPES,
    ...(clientId ? { clientId } : {}),
    ...(redirectURI ? { redirectURI } : {}),
    ...(state ? { state } : {}),
    ...(nonce ? { nonce } : {}),
  });
  return response;
};

// Exchanges the Apple identity payload for a QLearn session (backend verifies).
export const appleSignIn = (appleAuthData) => api.appleSignIn(appleAuthData);
